import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import SpikesGearHeader from './SpikesGearHeader';
import './Fulfillment.css';

const SIZE_ORDER = ['YS', 'YM', 'YL', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

const sizeSortIndex = (size) => {
  const idx = SIZE_ORDER.indexOf(size);
  return idx === -1 ? SIZE_ORDER.length : idx;
};

const Fulfillment = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      // Only ever "processing" orders — completed/picked-up orders are intentionally
      // excluded so staff can't accidentally re-print or over-fulfill something already done.
      const response = await api.get('/spikes-gear/orders', {
        params: { order_status: 'processing', limit: 1000 },
      });
      setOrders(response.data.orders || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch fulfillment queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Flatten every item across every processing order into a per-size print checklist
  const sizeGroups = {};
  orders.forEach((order) => {
    (order.items || []).forEach((item) => {
      const size = item.size || 'Unspecified';
      if (!sizeGroups[size]) sizeGroups[size] = [];
      sizeGroups[size].push({
        name_on_back: item.name_on_back,
        quantity: parseInt(item.quantity) || 0,
        customer_name: order.customer_name,
        order_id: order.id,
      });
    });
  });

  const sortedSizes = Object.keys(sizeGroups).sort(
    (a, b) => sizeSortIndex(a) - sizeSortIndex(b)
  );

  const totalShirts = orders.reduce(
    (sum, o) => sum + (o.items || []).reduce((s, i) => s + (parseInt(i.quantity) || 0), 0),
    0
  );

  const handleMarkCompleted = async (orderId) => {
    setCompletingId(orderId);
    try {
      await api.patch(`/spikes-gear/orders/${orderId}/status`, { order_status: 'completed' });
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (err) {
      alert('Failed to mark order completed');
    } finally {
      setCompletingId(null);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get('/spikes-gear/fulfillment/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `spikes-gear-fulfillment-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="spikes-gear-fulfillment">
      <SpikesGearHeader />

      <div className="fulfillment-scope-banner">
        Showing orders still <strong>IN PROCESS</strong> only — completed and picked-up orders are hidden here so nothing gets printed twice.
      </div>

      <div className="fulfillment-toolbar">
        <div className="fulfillment-summary">
          <span className="fulfillment-summary-value">{orders.length}</span>
          <span>order{orders.length === 1 ? '' : 's'} in process</span>
          <span className="fulfillment-summary-sep">·</span>
          <span className="fulfillment-summary-value">{totalShirts}</span>
          <span>shirt{totalShirts === 1 ? '' : 's'} to make</span>
        </div>
        <button className="btn-print" onClick={handleDownloadPDF} disabled={orders.length === 0 || downloading}>
          {downloading ? 'Generating PDF...' : '📄 Download PDF Checklist'}
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading fulfillment queue...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : orders.length === 0 ? (
        <div className="no-data">
          <p>Nothing in process — every order is completed or picked up. 🎉</p>
        </div>
      ) : (
        <div className="fulfillment-columns">
          {/* Left column: print checklist, grouped by size */}
          <div className="fulfillment-column">
            <div className="fulfillment-section-title">Print Checklist — Grouped by Size</div>
            <div className="fulfillment-column-scroll">
              <div className="print-checklist">
                {sortedSizes.map((size) => {
                  const rows = sizeGroups[size];
                  const sizeTotal = rows.reduce((sum, r) => sum + r.quantity, 0);
                  return (
                    <div className="size-group" key={size}>
                      <div className="size-group-header">
                        <span className="size-group-title">Size {size}</span>
                        <span className="size-group-count">{sizeTotal} shirt{sizeTotal === 1 ? '' : 's'}</span>
                      </div>
                      <table className="checklist-table">
                        <thead>
                          <tr>
                            <th>Name to Print</th>
                            <th>Qty</th>
                            <th>Customer / Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={i}>
                              <td className="checklist-name">{row.name_on_back}</td>
                              <td className="checklist-qty">x{row.quantity}</td>
                              <td className="checklist-customer">
                                {row.customer_name} <span className="checklist-order-id">(#{row.order_id})</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right column: orders in queue — full per-order breakdown, mark completed inline, no scrolling away */}
          <div className="fulfillment-column">
            <div className="fulfillment-section-title">Orders In Process ({orders.length})</div>
            <div className="fulfillment-column-scroll">
              <div className="queue-grid">
                {orders.map((order) => {
                  const shirtCount = (order.items || []).reduce(
                    (sum, i) => sum + (parseInt(i.quantity) || 0),
                    0
                  );
                  return (
                    <div className="queue-card" key={order.id}>
                      <div className="queue-card-header">
                        <div>
                          <span className="queue-card-order-id">#{order.id}</span>
                          <span className="queue-card-customer">{order.customer_name}</span>
                        </div>
                        <span className="queue-card-phone">{order.customer_phone}</span>
                      </div>

                      <table className="queue-card-items">
                        <tbody>
                          {(order.items || []).map((item, i) => (
                            <tr key={i}>
                              <td className="queue-item-name">{item.name_on_back}</td>
                              <td className="queue-item-size">{item.size}</td>
                              <td className="queue-item-qty">x{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div className="queue-card-footer">
                        <span className="queue-card-total">{shirtCount} shirt{shirtCount === 1 ? '' : 's'} total</span>
                        <button
                          className="btn-mark-completed"
                          onClick={() => handleMarkCompleted(order.id)}
                          disabled={completingId === order.id}
                        >
                          {completingId === order.id ? 'Marking...' : '✓ Mark Completed'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fulfillment;
